import { type PresetView } from "~/components/planning/presetView"
import { type SavedTableView, type ViewActor, type ViewAudit } from "~/server/api/routers/tableView"

/** What a view pill's details hover card describes: a saved view, or a preset with `details`. */
export type ViewDetailsSubject =
  | { view: SavedTableView; preset?: never }
  | { preset: PresetView; view?: never }

/**
 * The subject of a view pill's details hover card, or null when it gets none: the Default pill, a
 * preset without `details`, and any disabled pill, which keeps its tooltip with the reason.
 */
export function getViewDetailsSubject(opts: {
  view?: SavedTableView
  preset?: PresetView
  disabled: boolean
}): ViewDetailsSubject | null {
  if (opts.disabled) return null
  if (opts.view) return { view: opts.view }
  if (opts.preset?.details) return { preset: opts.preset }
  return null
}

/**
 * Normalized view-activity summary derived from redacted server audit metadata. `wasEdited` is
 * authoritative from the server (computed pre-redaction), so it stays reliable even when
 * creation fields are redacted. `latestActor`/`latestAt` are the editor's when present, else
 * the creator's.
 */
export type ViewActivity = {
  createdActor: ViewActor | null
  createdAt: string | null
  editedActor: ViewActor | null
  editedAt: string | null
  wasEdited: boolean
  latestActor: ViewActor | null
  latestAt: string | null
}

export function getViewActivity(audit: ViewAudit | undefined): ViewActivity | null {
  if (!audit) return null
  const latestAt = audit.editedAt ?? audit.createdAt
  const latestActor = audit.editedBy ?? audit.createdBy
  if (!latestAt && !latestActor) return null
  return {
    createdActor: audit.createdBy,
    createdAt: audit.createdAt,
    editedActor: audit.editedBy,
    editedAt: audit.editedAt,
    wasEdited: audit.wasEdited,
    latestActor,
    latestAt,
  }
}
